import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Star, Award, Users, ChefHat, Utensils } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";

const Home = () => {
  const [reviewRating, setReviewRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [reviewerName, setReviewerName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleStarClick = (rating) => {
    setReviewRating(rating);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (reviewRating === 0 || !reviewText.trim() || !reviewerName.trim()) {
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      
      // Reset form after success
      setTimeout(() => {
        setReviewRating(0);
        setReviewText("");
        setReviewerName("");
        setSubmitSuccess(false);
      }, 2000);
    }, 1500);
  };
  const testimonials = [
    {
      name: "Sarah & Michael Thompson",
      event: "Anniversary Dinner",
      text: "Zack transformed our dining room into a five-star restaurant. Every dish was perfectly crafted and the attention to detail was extraordinary.",
      rating: 5
    },
    {
      name: "Corporate Events Inc.",
      event: "Executive Dinner",
      text: "Professional, creative, and absolutely delicious. Our clients were thoroughly impressed with the quality and presentation.",
      rating: 5
    },
    {
      name: "The Williams Family",
      event: "Holiday Gathering",
      text: "Zack made our holiday celebration stress-free and memorable. The Italian feast was incredible and everyone is still talking about it!",
      rating: 5
    }
  ];

  const certifications = [
    "Johnson & Wales University - Science of Culinary Arts & Food Service Management (2015)",
    "University of Phoenix - Business Management, Operations & Entrepreneurship (2025)",
    "Wilderness Chef - Alaska Fishing Charter 'Cook Your Catch' Experience",
    "Farm-to-Table & Foraging Wild Ingredients Specialist",
    "Executive Chef & Pitmaster - Joe Bosco's Authentic Smokehouse BBQ",
    "Executive Chef - Hotel Fauchère (Relais & Châteaux Property)",
    "Elite Kitchen Experience: Ritz-Carlton (Grand Cayman & Denver)",
    "Yacht Charter Specialist - Paradise Marine Group Fort Lauderdale",
    "Event Management: Camden Yards & Capital One Arena VIP Services"
  ];

  return (
    <div className="page-home">
      {/* Hero Section */}
      <section className="hero-section relative py-20 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-25"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1703768516086-45eb97f36ce7')"
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-bg-warm/85 via-bg-primary/80 to-bg-accent/85"></div>
        
        <div className="container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Hero Content - Left Side */}
            <div className="lg:col-span-7 text-center lg:text-left">
              <h1 className="hero-large mb-6">
                Elevated Dining in the
                <span className="text-interactive-base block">Comfort of Your Home</span>
              </h1>
              <p className="body-large mb-8 max-w-2xl mx-auto lg:mx-0 text-secondary">
                Experience restaurant-quality cuisine crafted exclusively for you and your guests. 
                From intimate dinners to grand celebrations, every meal is a culinary journey.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/contact" className="btn-primary">
                  Book Your Experience
                </Link>
                <Link to="/portfolio" className="btn-secondary">
                  View My Work
                </Link>
              </div>
            </div>

            {/* Chef's Photo - Right Side */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div className="bg-gradient-to-br from-bg-accent to-bg-subtle rounded-none hover-lift overflow-hidden max-w-sm border-4 border-accent-warm/30">
                <div className="aspect-[4/5]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_culinary-elite/artifacts/uqoc5mbu_1000007587.jpg"
                    alt="Chef Zachary Kalmbach - Professional Headshot"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section className="section-padding relative overflow-hidden bg-warm">
        {/* Enhanced texture overlay */}
        <div className="absolute inset-0 opacity-15" style={{
          backgroundImage: "url('data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%235a7a6b' fill-opacity='0.15'%3E%3Cpath d='m0 40c0-5.5 4.5-10 10-10s10 4.5 10 10h-20zm10 0c0-5.5 4.5-10 10-10s10 4.5 10 10h-20zm10 0c0-5.5 4.5-10 10-10s10 4.5 10 10h-20z'/%3E%3C/g%3E%3C/svg%3E')"
        }}></div>
        
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6 bg-primary/90 backdrop-blur-sm p-8 border border-accent-warm/40 shadow-lg">
              <div>
                <h2 className="heading-1 mb-4 text-center text-interactive-base">Meet Chef Zachary</h2>
                <div className="w-16 h-1 bg-accent-warm mb-6 mx-auto"></div>
              </div>
              
              <p className="body-large">
                Chef Zachary Kalmbach is a highly accomplished and passionate private chef with over a decade of experience 
                dedicated to crafting unforgettable, high-end dining experiences. Specializing in personalized cuisine for 
                a diverse range of events, from intimate yacht charters and private residences to lavish weddings and corporate 
                gatherings, he brings a unique blend of creativity, precision, and luxury to every occasion.
              </p>
              
              <p className="body-regular text-secondary">
                His extensive culinary journey began with rigorous training at Johnson & Wales University in Denver, 
                where he earned a degree in Science of Culinary Arts & Food Service Management in 2015. This foundational 
                education, combined with his current degree in Business Management, Operations & Entrepreneurship from the 
                University of Phoenix (graduating March 2025), provides him with both artistic flair and exceptional 
                operational expertise.
              </p>
              
              <p className="body-regular text-secondary">
                His culinary journey has taken him from the wilderness of Alaska, where he worked with a fishing charter 
                offering their signature "Cook Your Catch" experience, to the elite kitchens of the Ritz-Carlton in Grand Cayman 
                Islands and Denver. In Alaska, he would set up field kitchens on rocky beaches, cooking fresh halibut and salmon 
                for guests right in the wild, or create five-course dinners in their lodge's private dining room with an open kitchen. 
                He paired their catch with foraged local ingredients—spruce tips, seaweed, wild berries, and mushrooms—creating 
                truly seasonal, hyper-local menus that connected guests to the Alaskan landscape.
              </p>
              
              <p className="body-regular text-secondary">
                This wilderness cooking experience perfectly prepared him for his role as Executive Chef at Hotel Fauchère, 
                a prestigious Relais & Châteaux property, where he embraced the organization's philosophy of hyper-local, 
                sustainable farm-to-table practices. This experience included managing their own farm and collaborating directly 
                with local farmers, most notably "Farmer Bill" of Liberty Hill Farms, an 80-year-old farmer who still works 
                his land by hand. He would plan crop selections and participate in harvesting, ensuring guests experienced 
                the true essence of seasonal, regional cuisine.
              </p>
              
              <p className="body-regular text-secondary">
                His mastery of live-fire cooking was further honed as Executive Chef and Pitmaster at Joe Bosco's Authentic Smokehouse BBQ, 
                where he embraced the ancient craft of Central Texas-style barbecue. Following in the tradition of legendary pitmasters 
                like Aaron Franklin and Tootsie Tomanetz, he would arrive before dawn to split wood, build fires, and tend the smoke 
                for 12-16 hours. He personally butchered and smoked all meats using post oak, applying only salt and pepper to let 
                the wood and fire speak through the meat—a discipline that demands patience, intuition, and respect for the craft that 
                spans generations.
              </p>
              
              <p className="body-regular text-secondary">
                He currently holds a position as Private Chef for Paradise Marine Group's yacht charters in Fort Lauderdale, 
                aboard the vessel Quantum, where he has become renowned for curating exquisite "Champagne & Caviar" dining experiences 
                that bring together his wilderness foraging expertise, fine dining background, and luxury yachting lifestyle.
              </p>

              <div className="space-y-4 bg-accent/70 p-6 border-l-4 border-accent-coral">
                <h4 className="heading-3 text-interactive-base">Education & Experience</h4>
                <ul className="space-y-2">
                  {certifications.map((cert, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <Award className="w-4 h-4 text-accent-coral" />
                      <span className="body-regular">{cert}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="space-y-4 bg-gradient-to-r from-bg-accent/60 to-accent-blue/30 p-6 border border-accent-blue/50">
                <h4 className="heading-3 text-accent-blue">Culinary Philosophy</h4>
                <p className="body-regular text-secondary">
                  <strong>Cooking is an act of love, respect, and responsibility.</strong> Every ingredient that enters my kitchen carries within it the story of the soil, the season, and the hands that nurtured it. My role is not to impose my will upon these gifts from nature, but to listen to what they want to become, to coax out their truest expression through technique that serves rather than overpowers.
                </p>
                
                <p className="body-regular text-secondary">
                  I believe in the profound intimacy of sharing a meal. When we sit together around a table, we create a sacred space where time slows, conversations deepen, and memories are etched into our hearts alongside the flavors on our tongues. Food becomes the medium through which we express care, celebrate milestones, comfort sorrows, and strengthen the bonds that make us human.
                </p>
                
                <p className="body-regular text-secondary">
                  <strong>Simplicity is the ultimate sophistication.</strong> The greatest dishes are often born from the humblest ingredients treated with reverence and patience. A perfectly ripe tomato needs little more than salt and time. Wild mushrooms ask only to be understood, not transformed. My job is to be their translator, speaking their language fluently enough to reveal their hidden poetry to those gathered at the table.
                </p>
                
                <p className="body-regular text-secondary">
                  Above all, I cook to create moments of joy—those fleeting instances when everything else falls away and there is only the perfect bite, the satisfied sigh, the spontaneous laughter that bubbles up when something delicious touches the soul. <strong>This is why we cook. This is why we gather. This is why food matters.</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="section-padding relative overflow-hidden">
        {/* More visible background pattern */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1651630535674-198c08635719')"
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-bg-accent/85 via-accent-coral/40 to-accent-blue/60"></div>
        
        <div className="container text-center relative z-10">
          <div className="bg-primary/70 backdrop-blur-md p-8 border border-accent-warm/40 shadow-xl">
            <h2 className="heading-1 mb-4 text-accent-blue">Culinary Services</h2>
            <p className="body-large text-secondary mb-12 max-w-2xl mx-auto">
              From intimate dinners to large celebrations, I create customized menus 
              tailored to your preferences and dietary needs.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
              <div className="text-center space-y-4 hover-lift bg-gradient-to-br from-bg-warm/90 to-bg-primary/90 backdrop-blur-sm p-8 border border-accent-warm/50">
                <Users className="w-12 h-12 text-accent-coral mx-auto" />
                <h3 className="heading-3 text-interactive-base">Private Dinners</h3>
                <p className="body-regular text-secondary">
                  Customized dining experience for 2-15 guests with personalized menu design.
                </p>
              </div>
              
              <div className="text-center space-y-4 hover-lift bg-gradient-to-br from-bg-warm/90 to-bg-primary/90 backdrop-blur-sm p-8 border border-accent-warm/50">
                <Utensils className="w-12 h-12 text-accent-blue mx-auto" />
                <h3 className="heading-3 text-interactive-base">Meal Prep Services</h3>
                <p className="body-regular text-secondary">
                  Weekly meal preparation with restaurant-quality dishes for your busy lifestyle.
                </p>
              </div>
              
              <div className="text-center space-y-4 hover-lift bg-gradient-to-br from-bg-warm/90 to-bg-primary/90 backdrop-blur-sm p-8 border border-accent-warm/50">
                <ChefHat className="w-12 h-12 text-accent-warm mx-auto" />
                <h3 className="heading-3 text-interactive-base">Cooking Classes</h3>
                <p className="body-regular text-secondary">
                  Learn professional techniques and secrets in hands-on cooking sessions.
                </p>
              </div>
              
              <div className="text-center space-y-4 hover-lift bg-gradient-to-br from-bg-warm/90 to-bg-primary/90 backdrop-blur-sm p-8 border border-accent-warm/50">
                <Award className="w-12 h-12 text-interactive-base mx-auto" />
                <h3 className="heading-3 text-interactive-base">Large Gatherings & Events</h3>
                <p className="body-regular text-secondary">
                  Impress clients and colleagues with exceptional catering and live cooking experiences.
                </p>
              </div>
            </div>
            
            <div className="mt-12">
              <Link to="/services" className="btn-primary">
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding relative">
        {/* Subtle grain texture */}
        <div className="absolute inset-0 opacity-3" style={{
          backgroundImage: "url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%236b8e7f' fill-opacity='0.05'%3E%3Ccircle cx='9' cy='9' r='2'/%3E%3Ccircle cx='49' cy='49' r='2'/%3E%3Ccircle cx='9' cy='49' r='1'/%3E%3Ccircle cx='49' cy='9' r='1'/%3E%3Ccircle cx='29' cy='29' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')"
        }}></div>
        
        <div className="container relative z-10">
          <div className="text-center mb-12">
            <h2 className="heading-1 mb-4">What Clients Say</h2>
            <div className="w-16 h-1 bg-interactive-base mx-auto"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-primary/90 backdrop-blur-sm p-8 hover-lift border border-border-light/30 relative">
                {/* Quote decoration */}
                <div className="absolute -top-2 -left-2 w-8 h-8 bg-interactive-base/20 flex items-center justify-center">
                  <span className="text-interactive-base font-serif text-xl">"</span>
                </div>
                
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current text-interactive-base" />
                  ))}
                </div>
                
                <blockquote className="body-regular text-secondary mb-6 italic">
                  "{testimonial.text}"
                </blockquote>
                
                <div className="border-t border-border-light pt-4">
                  <p className="body-small font-semibold">{testimonial.name}</p>
                  <p className="body-small text-light">{testimonial.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1523496922380-91d5afba98a3')"
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-bg-accent/90 to-bg-accent/70"></div>
        
        <div className="container text-center relative z-10">
          <h2 className="heading-1 mb-4">Ready for Your Culinary Experience?</h2>
          <p className="body-large text-secondary mb-8 max-w-xl mx-auto">
            Let's create something extraordinary together. Book your personalized 
            dining experience today.
          </p>
          <Link to="/contact" className="btn-primary">
            Start Planning Your Event
          </Link>
        </div>
      </section>

      {/* Leave a Review Section */}
      <section className="py-8 bg-primary">
        <div className="container text-center">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="bg-transparent border-interactive-base text-interactive-base hover:bg-interactive-base hover:text-white">
                Leave a Review
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle className="heading-2">Share Your Experience</DialogTitle>
                <DialogDescription className="body-regular">
                  Tell us about your dining experience with Chef Zachary
                </DialogDescription>
              </DialogHeader>
              
              {submitSuccess ? (
                <div className="text-center py-6">
                  <div className="mb-4">
                    {[...Array(reviewRating)].map((_, i) => (
                      <Star key={i} className="w-6 h-6 fill-current text-interactive-base inline-block" />
                    ))}
                  </div>
                  <p className="text-interactive-base font-medium mb-2">Thank you for your review!</p>
                  <p className="body-small text-secondary">Your feedback helps us continue to provide exceptional experiences.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmitReview} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reviewerName">Your Name *</Label>
                    <Input
                      id="reviewerName"
                      type="text"
                      value={reviewerName}
                      onChange={(e) => setReviewerName(e.target.value)}
                      placeholder="Enter your name"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Rating *</Label>
                    <div className="flex items-center justify-center space-x-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          className="p-1 hover:scale-110 transition-transform"
                          onClick={() => handleStarClick(star)}
                          onMouseEnter={() => setHoveredStar(star)}
                          onMouseLeave={() => setHoveredStar(0)}
                        >
                          <Star 
                            className={`w-8 h-8 transition-colors ${
                              star <= (hoveredStar || reviewRating) 
                                ? 'fill-current text-interactive-base' 
                                : 'text-border-medium'
                            }`} 
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reviewText">Your Review *</Label>
                    <Textarea
                      id="reviewText"
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder="Share your experience with Chef Zachary's culinary services..."
                      rows={4}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting || reviewRating === 0 || !reviewText.trim() || !reviewerName.trim()}
                    className="w-full bg-interactive-base hover:bg-interactive-hover text-white"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Submitting...</span>
                      </div>
                    ) : (
                      "Submit Review"
                    )}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </section>
    </div>
  );
};

export default Home;